import { otherProductReducer } from "app/otherProductReducer";
import AddToCartButton from "components/AddToCartButton";
import BillingCycleOptions from "components/BillingCycleOptions";
import CardWrapper from "components/CardWrapper";
import {
  addToCart,
  changeProductFromCart,
  removeCartItem,
} from "features/common/layouts/cartSlice";
import {
  getProduct,
  selectProduct,
} from "features/common/products/productsSlice";
import { formatIDR, useQuery } from "helpers/utils";
import MainLayouts from "layouts/MainLayouts";
import { useEffect, useReducer, useRef, useState } from "react";
import { Placeholder } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { notifySuccess } from "components/ToastMessage";
import { notifyError } from "components/ToastMessage";
import { setOrderParams } from "features/common/layouts/cartSlice";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

const OtherProduct = () => {
  const query = useQuery();
  const location = useLocation();
  const dispatch = useDispatch();
  const count = useRef(0);
  const { productsList } = useSelector((state) => state.cart);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("pid");
    const billingcycle = params.get("billingcycle");
    dispatch(setOrderParams({ pid, billingcycle }));
  }, [location.search, dispatch]);

  const [termin, setTermin] = useState(query.billingcycle || "annually");
  const [priceByTermin, setPriceByTermin] = useState(0);
  const isMonitimePid = +query.pid === 1146;
  const [isTrialChecked, setIsTrialChecked] = useState(isMonitimePid); // Default true untuk Monitime
  const [trialProduct, setTrialProduct] = useState(null);
  const [trialStatus, setTrialStatus] = useState("idle");
  const [trialCheckStatus, setTrialCheckStatus] = useState("idle");
  const [canOrderTrial, setCanOrderTrial] = useState(true);
  const [monitimeQuantity, setMonitimeQuantity] = useState(1);
  const [isManualCartUpdate, setIsManualCartUpdate] = useState(false);
  const { data_login } = useSelector((state) => state.auth);

  // Determine which PID to use based on trial checkbox
  const effectivePid = isMonitimePid && isTrialChecked ? 1157 : +query.pid || 921;
  const isTrialMode = isMonitimePid && isTrialChecked;

  // Inisialisasi termin berdasarkan query parameter billingcycle (hanya jika bukan trial mode)
  useEffect(() => {
    if (query.billingcycle && !isTrialMode) {
      setTermin(query.billingcycle);
    }
  }, [query.billingcycle, isTrialMode]);

  // Set billing cycle untuk trial mode
  useEffect(() => {
    if (isTrialMode) {
      // Untuk trial, gunakan billing cycle yang sesuai dengan durasi trial (14 hari)
      // Berdasarkan API response, trial menggunakan monthly billing cycle
      setTermin("monthly");
    } else if (query.billingcycle) {
      // Jika bukan trial mode dan ada query billingcycle, gunakan query tersebut
      setTermin(query.billingcycle);
    }
  }, [isTrialMode, query.billingcycle]);

  // Set default trial mode untuk Monitime
  useEffect(() => {
    if (isMonitimePid && !query.billingcycle) {
      // Jika Monitime dan tidak ada query billingcycle, set ke trial mode
      setIsTrialChecked(true);
      setTermin("monthly");
    }
  }, [isMonitimePid, query.billingcycle]);

  // Handler untuk trial checkbox
  const handleTrialCheckbox = (e) => {
    const checked = e.target.checked;
    setIsTrialChecked(checked);
    setIsManualCartUpdate(true);
    
    // Set billing cycle untuk trial mode
    if (checked) {
      setTermin("monthly"); // Trial menggunakan monthly billing cycle
    } else {
      // Jika uncheck trial, kembali ke query parameter atau default
      setTermin(query.billingcycle || "annually");
    }
    
    // Cek apakah ada produk di cart yang perlu diganti
    const existingCartItem = productsList.find(item => item.pid === 1146 || item.pid === 1157);
    
    if (checked) {
      // Jika check trial, langsung update cart item yang ada ke PID 1157
      if (existingCartItem) {
        const updatedTrialProduct = {
          ...existingCartItem,
          pid: 1157,
          name: "Monitime (Free Trial 14 Hari)",
          price: 0, // Trial is always free
          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
          configoptions: [
            {
              id: 115,
              value: monitimeQuantity - 1, // n-1 formula
            },
          ],
        };
        dispatch(changeProductFromCart(updatedTrialProduct));
      }
    } else {
      // Jika uncheck trial, hapus PID 1157 saja
      if (existingCartItem && existingCartItem.pid === 1157) {
        dispatch(removeCartItem(1157));
      }
    }
    
    // Reset flag after a short delay
    setTimeout(() => {
      setIsManualCartUpdate(false);
    }, 100);
  };

  // Handler untuk quantity Monitime
  const handleMonitimeQuantity = (e) => {
    const inputValue = parseInt(e.target.value);
    const newValue = isNaN(inputValue) || inputValue < 1 ? 1 : inputValue;
    setMonitimeQuantity(newValue);
    
    // Update cart jika produk sudah ada di cart
    const existingCartItem = productsList.find(item => item.pid === 1146 || item.pid === 1157);
    if (existingCartItem) {
      setIsManualCartUpdate(true);
      
      // Remove existing item first
      dispatch(removeCartItem(existingCartItem.pid));
      
      if (isTrialMode) {
        // Add trial product (PID 1157) with new quantity
        const trialProductDetails = {
          ...existingCartItem,
          pid: 1157,
          name: "Monitime (Free Trial 14 Hari)",
          price: 0, // Trial is always free
          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
          configoptions: [
            {
              id: 115,
              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
            },
          ],
        };
        dispatch(addToCart(trialProductDetails));
      } else {
        // Calculate base price per unit
        const basePricePerUnit = existingCartItem.price / (existingCartItem.configoptions?.[0]?.value + 1 || 1);
        
        // Add regular product (PID 1146) with new quantity
        const regularProductDetails = {
          ...existingCartItem,
          pid: 1146,
          name: "Monitime",
          price: basePricePerUnit * newValue, // Calculate total price based on quantity
          configoptions: [
            {
              id: 115,
              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
            },
          ],
        };
        dispatch(addToCart(regularProductDetails));
      }
      
      // Reset flag after a short delay
      setTimeout(() => {
        setIsManualCartUpdate(false);
      }, 100);
    }
  };

  const currentProduct = useSelector(selectProduct);
  const [otherProduct, setOtherProduct] = useReducer(otherProductReducer, {
    pid: effectivePid,
    price: {},
    productDetails: null,
  });
  const { pid, productDetails } = otherProduct;
  const { current, status } = currentProduct;

  useEffect(() => {
    dispatch(getProduct(effectivePid));
    // eslint-disable-next-line
  }, [effectivePid]);

  // Fetch trial product data when needed
  useEffect(() => {
    if (isMonitimePid) {
      setTrialStatus("loading");
      fetch(`${process.env.REACT_APP_BASE_API}apis/getProducts.php?pid=1157`)
        .then(res => res.json())
        .then(data => {
          if (data.result === "success" && data.products?.product?.[0]) {
            setTrialProduct(data.products.product[0]);
            setTrialStatus("idle");
          }
        })
        .catch(() => {
          setTrialStatus("error");
        });
    }
  }, [isMonitimePid]);

  // Check user trial status
  useEffect(() => {
    if (isMonitimePid && data_login?.client?.email) {
      setTrialCheckStatus("loading");
      fetch(`${process.env.REACT_APP_BASE_API}order/apis/checkMonitimeTrial.php?email=${encodeURIComponent(data_login.client.email)}`)
        .then(res => res.json())
        .then(data => {
          if (data.result === "success") {
            setCanOrderTrial(data.canOrder);
            setTrialCheckStatus("idle");
            
            // If user already has trial, uncheck trial checkbox
            if (!data.canOrder && isTrialChecked) {
              setIsTrialChecked(false);
            }
          } else {
            setTrialCheckStatus("error");
            setCanOrderTrial(false); // Default to false on error
          }
        })
        .catch(() => {
          setTrialCheckStatus("error");
          setCanOrderTrial(false); // Default to false on error
        });
    }
  }, [isMonitimePid, data_login?.client?.email, isTrialChecked]);

  useEffect(() => {
    if (current) {
      count.current += 1;
    }
    
    setOtherProduct({
      type: "SET_PRODUCT_DETAIL",
      payload: {
        ...current,
        // Add configoptions for Monitime PID 1146
        ...(isMonitimePid && !isTrialMode && {
          configoptions: [
            {
              id: 115,
              value: monitimeQuantity > 1 ? monitimeQuantity - 1 : 1, // Test with value 1 instead of 0
            },
          ],
        }),
        // Set billing cycle untuk trial mode
        ...(isTrialMode && {
          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
        }),
      },
    });
    setOtherProduct({
      type: "SET_PRODUCT_PRICE",
      payload: current?.pricing?.IDR,
    });
    setOtherProduct({
      type: "SET_PRICE_AND_CYCLE",
      payload: { price: priceByTermin, billingcycle: isTrialMode ? "monthly" : termin },
    });
  }, [current, termin, priceByTermin, isMonitimePid, isTrialMode, monitimeQuantity]);

  // Auto-add trial product to cart when trial is checked by default
  useEffect(() => {
    if (isMonitimePid && isTrialChecked && trialProduct && !productsList.find(item => item.pid === 1157)) {
      const trialProductDetails = {
        pid: 1157,
        name: "Monitime (Free Trial 14 Hari)",
        price: 0,
        billingcycle: "monthly",
        configoptions: [
          {
            id: 115,
            value: monitimeQuantity - 1,
          },
        ],
      };
      dispatch(addToCart(trialProductDetails));
    }
  }, [isMonitimePid, isTrialChecked, trialProduct, monitimeQuantity, productsList, dispatch]);

  useEffect(() => {
    switch (termin) {
      case "triennially":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.triennially);
        }
        break;
      case "biennially":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.biennially);
        }
        break;
      case "annually":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.annually);
        }
        break;
      case "quarterly":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.quarterly);
        }
        break;
      case "semiannually":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.semiannually);
        }
        break;
      case "monthly":
        if (isTrialMode) {
          setPriceByTermin(0); // Trial is free
        } else {
          setPriceByTermin(otherProduct?.price?.monthly);
        }
        break;
      default:
        setPriceByTermin(0);
        break;
    }
  }, [termin, dispatch, otherProduct, pid, isMonitimePid, isTrialMode]);

  const terminHandle = (e) => {
    const newTermin = e.target.value;
    setTermin(newTermin);
    
    // Update cart item dengan billing cycle baru
    const existingCartItem = productsList.find(item => item.pid === 1146 || item.pid === 1157);
    if (existingCartItem && productDetails) {
      setIsManualCartUpdate(true);
      
      // Remove existing item first
      dispatch(removeCartItem(existingCartItem.pid));
      
      // Calculate new price based on new billing cycle
      let newPrice = 0;
      let billingCycle = newTermin;
      
      if (isTrialMode) {
        newPrice = 0; // Trial is always free
        billingCycle = "monthly"; // Trial selalu menggunakan monthly billing cycle
      } else {
        newPrice = otherProduct?.price?.[newTermin] || 0;
        newPrice = newPrice * monitimeQuantity; // Apply quantity
      }
      
      // Add updated product with new billing cycle
      const updatedProductDetails = {
        ...productDetails,
        billingcycle: billingCycle,
        price: newPrice,
        configoptions: isMonitimePid ? [
          {
            id: 115,
            value: monitimeQuantity - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
          },
        ] : productDetails.configoptions,
      };
      
      dispatch(addToCart(updatedProductDetails));
      
      // Reset flag after a short delay
      setTimeout(() => {
        setIsManualCartUpdate(false);
      }, 100);
    }
  };

  const addToCartHandler = () => {
    if (status === "idle" && productDetails) {
      // Check if user is trying to order trial but already has one
      if (isTrialMode && !canOrderTrial) {
        notifyError("Anda sudah pernah menggunakan trial Monitime. Silakan pilih paket berbayar.");
        return;
      }
      
      // Calculate total price for Monitime based on quantity
      let finalProductDetails = { ...productDetails };
      if (isMonitimePid && !isTrialMode) {
        finalProductDetails = {
          ...productDetails,
          price: priceByTermin * monitimeQuantity, // Use priceByTermin instead of productDetails.price
          configoptions: [
            {
              id: 115,
              value: monitimeQuantity - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
            },
          ],
        };
      } else if (isTrialMode) {
        // Set billing cycle untuk trial mode
        finalProductDetails = {
          ...productDetails,
          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
          price: 0, // Trial is always free
          configoptions: [
            {
              id: 115,
              value: monitimeQuantity - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
            },
          ],
        };
      }
      
      dispatch(addToCart(finalProductDetails));
      notifySuccess("Produk berhasil ditambakan ke keranjang.");
    }
  };

  useEffect(() => {
    const checkProduct = productsList.find((list) => list.pid === query.pid);
    if (productDetails && checkProduct && !isMonitimePid && !isManualCartUpdate) {
      dispatch(changeProductFromCart(productDetails));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, dispatch, isMonitimePid, isManualCartUpdate]);

  // Update cart saat termin atau harga berubah (hanya untuk produk non-Monitime)
  useEffect(() => {
    const checkProduct = productsList.find((list) => list.pid === effectivePid);
    if (checkProduct && productDetails && status === "idle" && !isMonitimePid && !isManualCartUpdate) {
      // Pastikan billing cycle dan harga di cart match dengan state current
      if (checkProduct.billingcycle !== termin || checkProduct.price !== priceByTermin) {
        const updatedProductDetails = {
          ...productDetails,
          billingcycle: termin,
          price: priceByTermin,
        };
        dispatch(changeProductFromCart(updatedProductDetails));
      }
    }
  }, [termin, priceByTermin, productDetails, productsList, dispatch, effectivePid, query.billingcycle, status, isMonitimePid, isManualCartUpdate]);

  return (
    <MainLayouts summary={false}>
      <CardWrapper title="Konfigurasi Layanan">
        <div className="row">
          <div className="col-12">
            {status === "idle" && (
              <div className="fw-bold text-qw-primary mb-3 h5 product-title">
                {isTrialMode ? (
                  <>
                    Monitime (Free Trial 14 Hari)
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="monitime-tooltip">
                          Aplikasi Monitoring Website - Trial Gratis 14 Hari
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="ms-2"
                        style={{ fontSize: "14px", cursor: "help", color: "#FF6B35" }}
                      />
                    </OverlayTrigger>
                  </>
                ) : (
                  <>
                    {productDetails?.name}
                    {isMonitimePid && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="monitime-tooltip">
                            Aplikasi Monitoring Website
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          className="ms-2"
                          style={{ fontSize: "14px", cursor: "help", color: "#FF6B35" }}
                        />
                      </OverlayTrigger>
                    )}
                  </>
                )}
              </div>
            )}
            {status === "loading" && (
              <Placeholder as="div" animation="glow">
                <Placeholder xs={8} bg="danger" />
              </Placeholder>
            )}
          </div>
        </div>
        <div className="col-12 text-end">
          {status === "idle" && (
            <>
              {isTrialMode ? (
                <div className="d-flex flex-column align-items-end">
                  <div className="d-flex align-items-center">
                    <span className="badge bg-success p-2 me-2">
                      FREE TRIAL
                    </span>
                    <div className="fw-bold text-success h5 mb-0">
                      GRATIS
                    </div>
                  </div>
                  <small className="text-muted">14 hari trial</small>
                </div>
              ) : isMonitimePid ? (
                <div className="d-flex flex-column align-items-end">
                  <div className="fw-bold text-qw-primary h5 mb-0">
                    {formatIDR((priceByTermin ?? 0) * monitimeQuantity)}
                  </div>
                </div>
              ) : (
                <>
                  <label htmlFor="price" className="d-inline">
                    Harga:
                  </label>
                  <div className="fw-bold ms-3 mb-0 d-inline">
                    {formatIDR(priceByTermin ?? 0)}
                  </div>
                </>
              )}
            </>
          )}
          {status === "loading" && (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={2} />
            </Placeholder>
          )}
        </div>
        <div className="col-12">
          {status === "idle" && (
            <>
              {isTrialMode ? (
                <div className="mt-3">
                  <label htmlFor="activePeriod" className="mb-2 fw-bold">
                    Durasi
                  </label>
                  <select
                    className="form-select"
                    id="activePeriod"
                    name="activePeriod"
                    value="monthly"
                    disabled
                    style={{ width: "100%" }}
                  >
                    <option value="monthly">14 Hari (Trial)</option>
                  </select>
                </div>
              ) : (
                <BillingCycleOptions
                  termin={termin}
                  terminHandle={terminHandle}
                  productsItem={otherProduct}
                />
              )}
            </>
          )}
        </div>
        
        
        {/* Quantity Input untuk Monitime */}
        {isMonitimePid && !isTrialMode && status === "idle" && (
          <div className="col-12 mt-3">
            <div className="d-flex align-items-center">
              <label htmlFor="monitimeQuantity" className="form-label mb-0 me-3 fw-bold">
                Jumlah:
              </label>
              <div className="input-group" style={{ maxWidth: "120px" }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    const newValue = Math.max(1, monitimeQuantity - 1);
                    setMonitimeQuantity(newValue);
                    
                    // Update cart jika produk sudah ada di cart
                    const existingCartItem = productsList.find(item => item.pid === 1146 || item.pid === 1157);
                    if (existingCartItem) {
                      setIsManualCartUpdate(true);
                      
                      // Remove existing item first
                      dispatch(removeCartItem(existingCartItem.pid));
                      
                      if (isTrialMode) {
                        // Add trial product (PID 1157) with new quantity
                        const trialProductDetails = {
                          ...existingCartItem,
                          pid: 1157,
                          name: "Monitime (Free Trial 14 Hari)",
                          price: 0, // Trial is always free
                          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
                          configoptions: [
                            {
                              id: 115,
                              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
                            },
                          ],
                        };
                        dispatch(addToCart(trialProductDetails));
                      } else {
                        // Calculate base price per unit
                        const basePricePerUnit = existingCartItem.price / (existingCartItem.configoptions?.[0]?.value + 1 || 1);
                        
                        // Add regular product (PID 1146) with new quantity
                        const regularProductDetails = {
                          ...existingCartItem,
                          pid: 1146,
                          name: "Monitime",
                          price: basePricePerUnit * newValue, // Calculate total price based on quantity
                          configoptions: [
                            {
                              id: 115,
                              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
                            },
                          ],
                        };
                        dispatch(addToCart(regularProductDetails));
                      }
                      
                      // Reset flag after a short delay
                      setTimeout(() => {
                        setIsManualCartUpdate(false);
                      }, 100);
                    }
                  }}
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  className="form-control text-center"
                  id="monitimeQuantity"
                  value={monitimeQuantity}
                  onChange={handleMonitimeQuantity}
                  style={{ fontSize: "14px" }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    const newValue = monitimeQuantity + 1;
                    setMonitimeQuantity(newValue);
                    
                    // Update cart jika produk sudah ada di cart
                    const existingCartItem = productsList.find(item => item.pid === 1146 || item.pid === 1157);
                    if (existingCartItem) {
                      setIsManualCartUpdate(true);
                      
                      // Remove existing item first
                      dispatch(removeCartItem(existingCartItem.pid));
                      
                      if (isTrialMode) {
                        // Add trial product (PID 1157) with new quantity
                        const trialProductDetails = {
                          ...existingCartItem,
                          pid: 1157,
                          name: "Monitime (Free Trial 14 Hari)",
                          price: 0, // Trial is always free
                          billingcycle: "monthly", // Trial menggunakan monthly billing cycle
                          configoptions: [
                            {
                              id: 115,
                              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
                            },
                          ],
                        };
                        dispatch(addToCart(trialProductDetails));
                      } else {
                        // Calculate base price per unit
                        const basePricePerUnit = existingCartItem.price / (existingCartItem.configoptions?.[0]?.value + 1 || 1);
                        
                        // Add regular product (PID 1146) with new quantity
                        const regularProductDetails = {
                          ...existingCartItem,
                          pid: 1146,
                          name: "Monitime",
                          price: basePricePerUnit * newValue, // Calculate total price based on quantity
                          configoptions: [
                            {
                              id: 115,
                              value: newValue - 1, // n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
                            },
                          ],
                        };
                        dispatch(addToCart(regularProductDetails));
                      }
                      
                      // Reset flag after a short delay
                      setTimeout(() => {
                        setIsManualCartUpdate(false);
                      }, 100);
                    }
                  }}
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Trial Checkbox untuk Monitime */}
        {isMonitimePid && status === "idle" && (
          <div className="col-12 mt-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="trialCheckbox"
                checked={isTrialChecked}
                onChange={handleTrialCheckbox}
                disabled={!canOrderTrial || trialCheckStatus === "loading"}
              />
              <label className="ms-3 form-check-label" htmlFor="trialCheckbox">
                <span className={`fw-bold ${canOrderTrial ? "text-success" : "text-muted"}`}>
                  Free Trial 14 Hari
                </span>
                <br />
                <small className="text-muted">
                  {trialCheckStatus === "loading" ? (
                    "Memeriksa status trial..."
                  ) : canOrderTrial ? (
                    "Coba Monitime gratis selama 14 hari sebelum berlangganan"
                  ) : (
                    "Anda sudah pernah menggunakan trial Monitime"
                  )}
                </small>
              </label>
            </div>
          </div>
        )}
        <AddToCartButton onClick={addToCartHandler} />
      </CardWrapper>
    </MainLayouts>
  );
};

export default OtherProduct;
